import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Monitor, 
  Palette, 
  Coffee, 
  Calculator, 
  TrendingUp, 
  Table, 
  Keyboard, 
  Code, 
  Globe, 
  Megaphone, 
  Brain,
  X
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const courses = [
  { id: 'mscit', key: 'course.mscit', icon: Monitor, color: 'from-blue-500 to-blue-600' },
  { id: 'dtp', key: 'course.dtp', icon: Palette, color: 'from-pink-500 to-rose-500' },
  { id: 'java', key: 'course.java', icon: Coffee, color: 'from-orange-500 to-red-500' },
  { id: 'tally', key: 'course.tally', icon: Calculator, color: 'from-green-500 to-emerald-600' },
  { id: 'advTally', key: 'course.advTally', icon: TrendingUp, color: 'from-teal-500 to-cyan-600' },
  { id: 'excel', key: 'course.excel', icon: Table, color: 'from-green-600 to-green-700' },
  { id: 'typing', key: 'course.typing', icon: Keyboard, color: 'from-purple-500 to-violet-600' },
  { id: 'c', key: 'course.c', icon: Code, color: 'from-blue-600 to-indigo-600' },
  { id: 'python', key: 'course.python', icon: Code, color: 'from-yellow-500 to-amber-500' },
  { id: 'web', key: 'course.web', icon: Globe, color: 'from-cyan-500 to-blue-500' },
  { id: 'digital', key: 'course.digital', icon: Megaphone, color: 'from-red-500 to-pink-500' },
  { id: 'abacus', key: 'course.abacus', icon: Brain, color: 'from-cp-orange to-cp-orange-light' },
];

export default function Courses() {
  const { t } = useLanguage();
  const [selectedCourse, setSelectedCourse] = useState<string | null>(null);

  return (
    <section id="courses" className="section-padding bg-cp-blue-dark relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-cp-orange/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 -right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
      </div>

      <div className="container-custom relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('courses.title')}
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            {t('courses.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {courses.map((course, index) => {
            const Icon = course.icon;
            return (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                whileHover={{ y: -8, scale: 1.02 }}
                onClick={() => setSelectedCourse(course.id)}
                className="group cursor-pointer"
              >
                <div className="relative h-full glass rounded-2xl p-6 overflow-hidden card-3d">
                  {/* Gradient overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${course.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                  
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${course.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cp-orange transition-colors">
                    {t(course.key)}
                  </h3>
                  <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                    {t(`${course.key}.desc`)}
                  </p>
                  
                  {/* Learn more */}
                  <span className="text-cp-orange text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                    {t('courses.learnMore')}
                    <span className="transform group-hover:translate-x-1 transition-transform">→</span>
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Course Detail Modal */}
      <AnimatePresence>
        {selectedCourse && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setSelectedCourse(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass rounded-2xl p-8 max-w-md w-full relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedCourse(null)}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              
              {(() => {
                const course = courses.find(c => c.id === selectedCourse);
                if (!course) return null;
                const Icon = course.icon;
                
                return (
                  <>
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${course.color} flex items-center justify-center mb-6`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4">
                      {t(course.key)}
                    </h3>
                    <p className="text-gray-300 mb-6">
                      {t(`${course.key}.desc`)}
                    </p>
                    <a
                      href="#contact"
                      onClick={() => setSelectedCourse(null)}
                      className="btn-primary w-full py-3 rounded-xl text-center font-semibold text-white block"
                    >
                      {t('contact.enquiry')}
                    </a>
                  </>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}